import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q8id4nlsngrm4lltcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg =
          (parsed as Record<string, unknown>)?.meta && (parsed as Record<string, unknown>).meta
            ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message || ((parsed as Record<string, unknown>).meta as Record<string, string>).detail
            : '';
        if (serverMsg && (serverMsg as string).includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg as string || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于吃瓜视频',
    description: '吃瓜视频平台介绍，了解我们的品牌理念、用户口碑与联系方式。',
    url: 'https://readyue.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜视频',
      url: 'https://readyue.cn',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '吃瓜视频平台的内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们平台每日实时更新，确保您不错过任何最新最热的吃瓜内容。编辑团队全天候追踪娱乐圈和社会热点，第一时间为您带来新鲜热辣的瓜事报道。',
        },
      },
      {
        '@type': 'Question',
        name: '如何快速找到我感兴趣的吃瓜内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过顶部的分类导航快速筛选不同类别的内容，包括明星吃瓜、娱乐八卦、社会热点、综艺爆料、网红动态等多个频道。',
        },
      },
      {
        '@type': 'Question',
        name: '吃瓜视频平台的内容来源是否可靠？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们坚持从权威媒体和可靠渠道获取信息，所有内容都经过编辑团队的严格审核，力求在传播过程中保持客观公正。',
        },
      },
      {
        '@type': 'Question',
        name: '可以在手机上看吃瓜视频吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '当然可以！吃瓜视频平台采用响应式设计，完美适配手机、平板和电脑等多种设备。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向我们提供新闻线索或爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '如果您有独家爆料或新闻线索，欢迎通过关于我们页面的联系方式与我们取得联系。',
        },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <ScrollToTop />
      <main>
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

        <div className="w-full px-4 md:px-6 pt-6 pb-4">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <header className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">关于吃瓜视频</h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              吃瓜视频是一个致力于为广大群众提供最新、最热、最全娱乐八卦与社会热点视频内容的聚合平台。
              我们相信，吃瓜不仅是一种娱乐方式，更是一种生活态度。
            </p>
          </div>
        </header>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100/50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                  <h2 className="text-lg md:text-xl font-semibold text-foreground-900">品牌故事</h2>
                </div>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    吃瓜视频诞生于2023年，由一群热爱娱乐文化与新闻资讯的年轻人共同创立。我们注意到，随着社交媒体的快速发展，各类娱乐八卦与社会热点信息层出不穷，"吃瓜"已经成为当代网民不可或缺的日常消遣。
                  </p>
                  <p>
                    然而，信息的碎片化和分散化使得用户想要一站式获取完整、真实的瓜事变得困难。于是，吃瓜视频应运而生——我们致力于打造一个集明星吃瓜、娱乐八卦、社会热点、综艺爆料、网红动态于一体的视频聚合平台。
                  </p>
                  <p>
                    经过三年的发展，吃瓜视频已经成长为国内领先的娱乐资讯视频平台之一，日均活跃用户超过百万，累计服务了数千万热爱吃瓜的用户。
                  </p>
                </div>
              </div>
              <div>
                <div className="relative w-full aspect-video rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20office%20space%20with%20young%20Chinese%20team%20brainstorming%20around%20a%20table%20with%20laptops%20and%20colorful%20post-it%20notes%2C%20warm%20natural%20light%20streaming%20through%20large%20windows%2C%20casual%20creative%20atmosphere%2C%20minimal%20design%2C%20candid%20documentary%20style%20photography&width=800&height=450&seq=about-brand-2026&orientation=landscape"
                    alt="吃瓜视频团队工作场景"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-1 h-5 rounded-full bg-accent-500"></div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground-900">用户口碑</h2>
              </div>
              <p className="text-sm text-foreground-500">听听我们的用户怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  name: '小明同学',
                  avatar: 'https://readdy.ai/api/search-image?query=A%20young%20Chinese%20person%20portrait%20with%20friendly%20smile%2C%20casual%20style%2C%20minimal%20studio%20background%2C%20warm%20lighting%2C%20professional%20headshot&width=120&height=120&seq=avatar-t1&orientation=squarish',
                  comment: '每天打开吃瓜视频已经成了我的习惯！更新速度超快，内容也很有意思，再也不用到处找瓜吃了！',
                  role: '忠实用户 · 使用1年',
                },
                {
                  name: '娱乐达人小王',
                  avatar: 'https://readdy.ai/api/search-image?query=A%20trendy%20young%20Chinese%20woman%20portrait%20with%20confident%20expression%2C%20modern%20style%2C%20minimal%20studio%20background%2C%20warm%20lighting%2C%20professional%20headshot&width=120&height=120&seq=avatar-t2&orientation=squarish',
                  comment: '分类做得很好，想看什么类型的瓜都能快速找到。明星吃瓜频道是我的最爱，每次都有惊喜！',
                  role: '娱乐博主 · 使用8个月',
                },
                {
                  name: '吃瓜群众老李',
                  avatar: 'https://readdy.ai/api/search-image?query=A%20middle%20aged%20Chinese%20man%20portrait%20with%20warm%20smile%2C%20casual%20professional%20style%2C%20minimal%20studio%20background%2C%20warm%20lighting%2C%20professional%20headshot&width=120&height=120&seq=avatar-t3&orientation=squarish',
                  comment: '内容质量不错，不只是简单的标题党，每篇文章都有料。社会热点板块也做得很好，不止于娱乐。',
                  role: '媒体从业者 · 使用6个月',
                },
              ].map((item) => (
                <div key={item.name} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                      <p className="text-[10px] text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground-900">联系我们</h2>
              </div>
              <p className="text-sm text-foreground-500">
                有新闻线索、合作意向或任何问题？欢迎随时联系我们！
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">电子邮箱</h3>
                    <p className="text-xs text-foreground-500">contact@readyue.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">客服电话</h3>
                    <p className="text-xs text-foreground-500">400-888-6666</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">公司地址</h3>
                    <p className="text-xs text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座15层</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-foreground-100 text-foreground-600">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">工作时间</h3>
                    <p className="text-xs text-foreground-500">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 rounded-lg border border-background-300 text-sm bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                      placeholder="请描述您的问题或建议（最多500字）"
                    ></textarea>
                    <p className="text-[10px] text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="antispam-field"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '提交中...' : '发送留言'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-600">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-red-500">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-1 h-5 rounded-full bg-accent-500"></div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground-900">常见问题</h2>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { q: '吃瓜视频平台的内容多久更新一次？', a: '我们平台每日实时更新，确保您不错过任何最新最热的吃瓜内容。编辑团队全天候追踪娱乐圈和社会热点，第一时间为您带来新鲜热辣的瓜事报道。' },
                { q: '如何快速找到我感兴趣的吃瓜内容？', a: '您可以通过顶部的分类导航快速筛选不同类别的内容，包括明星吃瓜、娱乐八卦、社会热点、综艺爆料、网红动态等多个频道。同时，首页的热门推荐也会帮您发现当下最火的瓜事。' },
                { q: '吃瓜视频平台的内容来源是否可靠？', a: '我们坚持从权威媒体和可靠渠道获取信息，所有内容都经过编辑团队的严格审核。虽然娱乐八卦内容本身具有不确定性，但我们力求在传播过程中保持客观公正，避免传播未经证实的虚假信息。' },
                { q: '可以在手机上看吃瓜视频吗？', a: '当然可以！吃瓜视频平台采用响应式设计，完美适配手机、平板和电脑等多种设备。无论您身在何处，随时随地都能愉快吃瓜。' },
                { q: '如何向我们提供新闻线索或爆料？', a: '如果您有独家爆料或新闻线索，欢迎通过本页面的联系方式与我们取得联系。我们重视每一条用户反馈，对于有价值的线索，编辑团队会第一时间跟进核实。' },
                { q: '吃瓜视频平台是否需要注册账号？', a: '目前吃瓜视频平台完全免费开放，无需注册即可浏览全部内容。我们致力于让每一位用户无障碍地享受吃瓜的乐趣。' },
              ].map((item, idx) => (
                <details key={idx} className="group rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors list-none">
                    {item.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}